import { useCallback, useEffect, useRef, useState } from 'react'
import { osFetch } from 'util/fetch'
import { chatDrawerActions } from '../../ducks/chatDrawer'
import { CLIENT_SIDE_TOOLS } from '../tools/clientSideTools'
import { handleToolCall } from './handlers/handleToolCall'
import { RealtimeClientConfig, VoiceProviderProps } from './types'
import { getChatContextState } from './utils/chatContextUtils'
import { importVoiceToChat } from './utils/importVoiceToChat'
import {
  buildGaSessionUpdate,
  buildSessionAudioInput,
  DEFAULT_TURN_DETECTION,
  EMPTY_REALTIME_CLIENT_CONFIG,
  extractAssistantTranscriptsFromResponseDone,
  extractFunctionCallsFromEvent,
  extractMessageContentText,
  extractReasoningTextDelta,
  getAssistantTranscriptFragment,
  mergeAssistantTranscriptFragment,
  isAdaAudioResponseEvent,
  isAssistantAudioPlaybackEvent,
  isAssistantAudioPlaybackStoppedEvent,
  isAssistantTranscriptFragmentEvent,
  isFunctionCallOnlyResponseDone,
  isResponseLifecycleStartEvent,
  revealNextSpeakingTranscriptChunk,
  shouldClearPreambleForAssistantSpeech,
  VOICE_REASONING_STATUS_FALLBACK,
} from './utils/realtimeSessionUtils'
import { VoiceContext } from './VoiceContext'

/** ~2 words/sec reveal — closer to spoken cadence than raw transcript deltas. */
const SPEAKING_REVEAL_INTERVAL_MS = 130

export const VoiceProvider = ({ children, history }: VoiceProviderProps) => {
  const [logs, setLogs] = useState<any[]>([])
  const [toolCall, setToolCall] = useState<any>(null)
  const [isSessionStarted, setIsSessionStarted] = useState(false)
  const [isSessionActive, setIsSessionActive] = useState(false)
  const [isSessionCreated, setIsSessionCreated] = useState(false)
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [dynamicTools, setDynamicTools] = useState<any[]>(CLIENT_SIDE_TOOLS)
  const [realtimeClientConfig, setRealtimeClientConfig] = useState<RealtimeClientConfig>(EMPTY_REALTIME_CLIENT_CONFIG)
  const [hasSentWelcomeMessage, setHasSentWelcomeMessage] = useState(false)
  const [userHasSpoken, setUserHasSpoken] = useState(false)
  const [isListening, setIsListening] = useState(false)
  const [orgId, setOrgId] = useState<number | null>(null)
  const [osAssistantCode, setOsAssistantCode] = useState<string | null>(null)
  const orgIdRef = useRef<number | null>(null)
  const osAssistantCodeRef = useRef<string | null>(null)

  useEffect(() => {
    orgIdRef.current = orgId
  }, [orgId])

  useEffect(() => {
    osAssistantCodeRef.current = osAssistantCode
  }, [osAssistantCode])

  const [sessionTranscriptions, setSessionTranscriptions] = useState<
    Array<{
      content: string
      direction: 'send' | 'receive'
      timestamp: number
      sharedWithContexts: string[]
    }>
  >([])

  const sessionTranscriptionsRef = useRef(sessionTranscriptions)

  useEffect(() => {
    sessionTranscriptionsRef.current = sessionTranscriptions
  }, [sessionTranscriptions])

  const [sessionToolCalls, setSessionToolCalls] = useState<
    Array<{
      toolName: string
      arguments: any
      timestamp: number
      failed?: boolean
      error?: string
    }>
  >([])

  const sessionToolCallsRef = useRef(sessionToolCalls)

  useEffect(() => {
    sessionToolCallsRef.current = sessionToolCalls
  }, [sessionToolCalls])

  const [dataChannel, setDataChannel] = useState<RTCDataChannel | null>(null)
  const dataChannelRef = useRef<RTCDataChannel | null>(null)
  const peerConnection = useRef<RTCPeerConnection | null>(null)
  const audioElement = useRef<HTMLAudioElement | null>(null)
  const [audioStream, setAudioStream] = useState<MediaStream | null>(null)
  const audioStreamRef = useRef<MediaStream | null>(null)
  const audioTransceiver = useRef<RTCRtpTransceiver | null>(null)
  const tracks = useRef<RTCRtpSender[] | null>(null)

  const isConnecting = isSessionStarted && !isSessionActive
  const [isResponding, setIsResponding] = useState(false)
  const isRespondingRef = useRef(false)
  const [isPreparingResponse, setIsPreparingResponse] = useState(false)
  const [reasoningStatus, setReasoningStatus] = useState<string | null>(null)
  const [speakingTranscript, setSpeakingTranscript] = useState<string | null>(null)
  const reasoningStatusRef = useRef<string | null>(null)
  const speakingTranscriptTargetRef = useRef('')
  const speakingTranscriptDisplayRef = useRef('')
  const speakingRevealTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const pendingAssistantTranscriptRef = useRef('')
  const isAssistantAudioPlayingRef = useRef(false)
  const isPreparingResponseRef = useRef(false)
  const userHasSpokenRef = useRef(false)

  // Track mounting status to prevent state updates after unmount
  const isMountedRef = useRef(true)
  // Prevent concurrent session operations
  const sessionOperationInProgressRef = useRef(false)

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  // App state - make it optional and safe
  const [chatContextState, setChatContextState] = useState<Record<string, any>>({})

  const getDefaultChatContextState = () => {
    return {
      tools: [],
      instructions: '',
      app_state: {},
    }
  }

  const currentSessionStateAppliedRef = useRef<any>(getDefaultChatContextState())
  const processedFunctionCallIdsRef = useRef<Set<string>>(new Set())

  function getDataChannelInCallback() {
    return dataChannelRef?.current || null
  }

  const clearReasoningPreamble = useCallback(() => {
    reasoningStatusRef.current = null
    isPreparingResponseRef.current = false
    if (isMountedRef.current) {
      setReasoningStatus(null)
      setIsPreparingResponse(false)
    }
  }, [])

  const updateReasoningPreamble = useCallback((text: string) => {
    const cleaned = text.replace(/\n+/g, ' ').trim()
    if (!cleaned) {
      return
    }
    reasoningStatusRef.current = cleaned
    isPreparingResponseRef.current = true
    if (isMountedRef.current) {
      setReasoningStatus(cleaned)
      setIsPreparingResponse(true)
      setIsListening(false)
    }
  }, [])

  const appendReasoningPreamble = useCallback(
    (delta: string) => {
      const current = reasoningStatusRef.current
      const base = current === VOICE_REASONING_STATUS_FALLBACK || !current ? '' : current
      updateReasoningPreamble(`${base}${delta}`)
    },
    [updateReasoningPreamble]
  )

  const beginAssistantTurn = useCallback(() => {
    updateReasoningPreamble(VOICE_REASONING_STATUS_FALLBACK)
  }, [updateReasoningPreamble])

  const stopSpeakingRevealTimer = useCallback(() => {
    if (speakingRevealTimerRef.current) {
      clearInterval(speakingRevealTimerRef.current)
      speakingRevealTimerRef.current = null
    }
  }, [])

  const syncSpeakingTranscriptDisplay = useCallback((text: string) => {
    const cleaned = text.replace(/\s+/g, ' ').trim()
    speakingTranscriptDisplayRef.current = cleaned
    if (isMountedRef.current) {
      setSpeakingTranscript(cleaned || null)
    }
  }, [])

  const clearSpeakingTranscript = useCallback(() => {
    stopSpeakingRevealTimer()
    speakingTranscriptTargetRef.current = ''
    speakingTranscriptDisplayRef.current = ''
    if (isMountedRef.current) {
      setSpeakingTranscript(null)
    }
  }, [stopSpeakingRevealTimer])

  const tickSpeakingTranscriptReveal = useCallback(() => {
    const target = speakingTranscriptTargetRef.current
    const displayed = speakingTranscriptDisplayRef.current
    if (displayed.length >= target.length) {
      return
    }
    const next = revealNextSpeakingTranscriptChunk(displayed, target)
    syncSpeakingTranscriptDisplay(next)
  }, [syncSpeakingTranscriptDisplay])

  const startSpeakingRevealTimer = useCallback(() => {
    if (speakingRevealTimerRef.current) {
      return
    }
    speakingRevealTimerRef.current = setInterval(() => {
      tickSpeakingTranscriptReveal()
    }, SPEAKING_REVEAL_INTERVAL_MS)
    tickSpeakingTranscriptReveal()
  }, [tickSpeakingTranscriptReveal])

  const appendSpeakingTranscriptTarget = useCallback((fragment: string) => {
    if (!fragment) {
      return
    }
    speakingTranscriptTargetRef.current = mergeAssistantTranscriptFragment(
      speakingTranscriptTargetRef.current,
      fragment
    )
  }, [])

  const resumeAssistantPlayback = useCallback(() => {
    const audio = audioElement.current
    if (!audio) {
      return
    }
    if (audio.paused) {
      audio.play().catch((error) => {
        console.warn('[Voice] Failed to resume assistant playback:', error)
      })
    }
  }, [])

  const setAssistantResponding = useCallback(
    (responding: boolean) => {
      isRespondingRef.current = responding
      if (responding) {
        resumeAssistantPlayback()
      }
      if (isMountedRef.current) {
        setIsResponding(responding)
        setIsListening(!responding)
      }
    },
    [resumeAssistantPlayback]
  )

  const stopAssistantPlayback = useCallback(() => {
    isAssistantAudioPlayingRef.current = false
    if (audioElement.current) {
      audioElement.current.pause()
      audioElement.current.currentTime = 0
    }
  }, [])

  // Send a message to the model
  const sendClientEvent = useCallback((message: any) => {
    const _dataChannel = getDataChannelInCallback()

    if (_dataChannel?.readyState === 'open') {
      message.event_id = message.event_id || crypto.randomUUID()
      console.log('sendClientEvent:', message)
      _dataChannel.send(JSON.stringify(message))
    } else {
      console.error('Failed to send message - data channel not available or not open', _dataChannel?.readyState)
    }
  }, [])

  const interruptAssistantIfSpeaking = useCallback(() => {
    const shouldInterrupt =
      isAssistantAudioPlayingRef.current || isPreparingResponseRef.current || isRespondingRef.current
    if (!shouldInterrupt) {
      return
    }
    stopAssistantPlayback()
    sendClientEvent({ type: 'response.cancel' })
    setAssistantResponding(false)
    clearReasoningPreamble()
    clearSpeakingTranscript()
  }, [clearReasoningPreamble, clearSpeakingTranscript, sendClientEvent, setAssistantResponding, stopAssistantPlayback])

  // Update currentSessionStateAppliedRef when tools change
  useEffect(() => {
    const newTools = realtimeClientConfig?.tools?.concat(dynamicTools)
    if (currentSessionStateAppliedRef.current.tools !== newTools) {
      currentSessionStateAppliedRef.current.tools = newTools

      // when dynamicTools changes, update the session config
      if (dataChannel && dataChannel.readyState === 'open' && realtimeClientConfig) {
        const sessionUpdate = buildGaSessionUpdate({
          tools: currentSessionStateAppliedRef.current.tools,
          audio: buildSessionAudioInput(realtimeClientConfig.input_audio_transcription),
        })
        sendClientEvent(sessionUpdate)
      }
    }
  }, [realtimeClientConfig?.tools, dynamicTools, sendClientEvent, dataChannel])

  // Update the data channel ref whenever the state changes
  useEffect(() => {
    dataChannelRef.current = dataChannel
  }, [dataChannel])

  // Update the audio stream ref whenever the state changes
  useEffect(() => {
    audioStreamRef.current = audioStream
  }, [audioStream])

  // Define handleToolCall outside useEffect so it can be recreated when tools change
  const handleToolCallCallback = useCallback(
    async (output: any) => {
      await handleToolCall({
        output,
        history,
        osAssistantCode: osAssistantCodeRef.current,
        orgId: orgIdRef.current,
        isMountedRef,
        setToolCall,
        setSessionToolCalls,
        getDataChannelInCallback,
        sendClientEvent,
        currentSessionStateAppliedRef,
      })
    },
    [history, sendClientEvent]
  )

  // Get chat context state safely
  const getChatContextStateCallback = useCallback(() => {
    return getChatContextState(history)
  }, [history])

  // Update voice chat context when pathname changes
  useEffect(() => {
    const newChatContextState = getChatContextStateCallback()
    if (isMountedRef.current) {
      setChatContextState(newChatContextState)
    }
  }, [getChatContextStateCallback])

  const runFunctionCallsFromEvent = useCallback(
    (event: any) => {
      for (const functionCall of extractFunctionCallsFromEvent(event)) {
        if (!functionCall.call_id || processedFunctionCallIdsRef.current.has(functionCall.call_id)) {
          continue
        }
        processedFunctionCallIdsRef.current.add(functionCall.call_id)

        isAssistantAudioPlayingRef.current = false
        setAssistantResponding(false)
        clearReasoningPreamble()

        handleToolCallCallback(functionCall).catch((error) => {
          console.error('[Voice] Unhandled tool call error:', error)
          const _dataChannel = getDataChannelInCallback()
          if (_dataChannel && _dataChannel.readyState === 'open') {
            try {
              sendClientEvent({
                type: 'conversation.item.create',
                item: {
                  type: 'function_call_output',
                  call_id: functionCall.call_id,
                  output: JSON.stringify({
                    response: `Tool call failed: ${error.message || 'Unknown error'}`,
                    error: error.message || 'Unknown error',
                  }),
                },
              })
              sendClientEvent({ type: 'response.create' })
            } catch (sendError) {
              console.error('[Voice] Failed to send error response:', sendError)
            }
          }
        })
      }
    },
    [clearReasoningPreamble, handleToolCallCallback, sendClientEvent, setAssistantResponding]
  )

  // Set up data channel for sending and receiving events with improved connection handling
  const setupDataChannel = useCallback(
    (
      pc: RTCPeerConnection,
      osAssistantCode: string,
      orgId: number,
      sessionId: string,
      clientConfig: RealtimeClientConfig,
      mergedTools: any[]
    ) => {
      try {
        const dc = pc.createDataChannel('oai-events', {
          ordered: true,
        })

        // Handle data channel state changes
        dc.onopen = () => {
          console.log('Data channel opened successfully with readyState:', dc.readyState)
          setIsSessionActive(true)
          // Don't set isListening(true) immediately - wait for welcome message to be sent first
          // This prevents showing "Listening..." before Ada greets the user

          if (isMountedRef.current) {
            setLogs([])
          }

          let instructions = clientConfig.instructions || ''

          const chatDrawerState = window.reduxStore?.getState()?.chatDrawer

          const getUnsharedContextSuffix = () => {
            const chatUnsharedQueue =
              chatDrawerState?.messages?.filter((msg) => !msg.sharedWithContexts.includes('voice')) || []

            if (chatUnsharedQueue.length === 0) {
              return ''
            }

            const contextMessages = chatUnsharedQueue
              .map((msg) => `${msg.direction === 'send' ? 'User' : 'Assistant'}: ${msg.content}`)
              .join('\n')

            return `\n===PREVIOUS CONVERSATION CONTEXT===\n${contextMessages}\n===END CONTEXT===`
          }

          const unsharedContextSuffix = getUnsharedContextSuffix()

          let instructionsPlusAppState = `${instructions}${unsharedContextSuffix}\n\nAPP_STATE: ${JSON.stringify(
            chatContextState || {}
          )}`

          // Send session config
          try {
            currentSessionStateAppliedRef.current.tools = mergedTools

            const sessionConfig = {
              ...buildGaSessionUpdate({
                tools: mergedTools,
                instructions: instructionsPlusAppState,
                audio: buildSessionAudioInput(clientConfig.input_audio_transcription, DEFAULT_TURN_DETECTION),
              }),
              event_id: crypto.randomUUID(),
            }
            if (window.adaDebug) {
              console.log('DEBUG: Sending main session update with transcription settings:', sessionConfig)
            }
            dc.send(JSON.stringify(sessionConfig))

            // Mark as applied to the session state
            currentSessionStateAppliedRef.current.app_state = chatContextState

            const chatUnsharedQueue =
              chatDrawerState?.messages?.filter((msg) => !msg.sharedWithContexts.includes('voice')) || []
            if (window.reduxStore && chatUnsharedQueue.length > 0) {
              window.reduxStore.dispatch(chatDrawerActions.markMessagesSharedWithContext('voice'))
            }
          } catch (err) {
            console.error('Error sending initial session update', err)
          }
        }

        dc.onclose = () => {
          console.log('Data channel closed')
          if (isMountedRef.current) {
            setIsSessionActive(false)
          }
        }

        dc.onerror = (error) => {
          console.error('Data channel error:', error)
        }

        // Set up message handling
        dc.onmessage = (e: MessageEvent) => {
          try {
            const event = JSON.parse(e.data)
            if (window.adaDebug) {
              console.log('message received:', event)
            }
            if (isResponseLifecycleStartEvent(event.type)) {
              pendingAssistantTranscriptRef.current = ''
              clearSpeakingTranscript()
              if (userHasSpokenRef.current && !reasoningStatusRef.current) {
                beginAssistantTurn()
              }
            } else if (isAssistantTranscriptFragmentEvent(event)) {
              const fragment = getAssistantTranscriptFragment(event)
              if (fragment) {
                pendingAssistantTranscriptRef.current = mergeAssistantTranscriptFragment(
                  pendingAssistantTranscriptRef.current,
                  fragment
                )
                appendSpeakingTranscriptTarget(fragment)
                if (isAssistantAudioPlayingRef.current || isRespondingRef.current) {
                  startSpeakingRevealTimer()
                }
              }
            } else {
              const reasoningDelta = extractReasoningTextDelta(event)
              if (reasoningDelta) {
                appendReasoningPreamble(reasoningDelta)
              }
            }

            if (shouldClearPreambleForAssistantSpeech(event.type)) {
              clearReasoningPreamble()
            }

            if (isAssistantAudioPlaybackEvent(event.type)) {
              isAssistantAudioPlayingRef.current = true
              resumeAssistantPlayback()
              if (speakingTranscriptTargetRef.current) {
                startSpeakingRevealTimer()
              }
            } else if (isAssistantAudioPlaybackStoppedEvent(event.type)) {
              isAssistantAudioPlayingRef.current = false
            }

            if (isAdaAudioResponseEvent(event.type)) {
              if (window.adaDebug) {
                console.log('Ada responding (audio/transcript delta) - setting responding=true')
              }
              setAssistantResponding(true)
            } else if (isAssistantAudioPlaybackStoppedEvent(event.type)) {
              if (window.adaDebug) {
                console.log('Ada finished or interrupted speaking - back to listening')
              }
              clearSpeakingTranscript()
              clearReasoningPreamble()
              setAssistantResponding(false)
            } else if (event.type === 'input_audio_buffer.speech_started') {
              if (window.adaDebug) {
                console.log('User speech started - interrupting if Ada is speaking')
              }
              interruptAssistantIfSpeaking()
            }

            const EVENT_TYPES_TO_LOG = [
              'response.done',
              'session.updated',
              'session.created',
              'conversation.item.input_audio_transcription.completed',
            ]

            if (EVENT_TYPES_TO_LOG.includes(event.type)) {
              if (window.adaDebug) {
                console.log('log message')
              }

              // Log the event
              if (osAssistantCode && orgId && sessionId) {
                osFetch(`${window.API_ROOT}/api/ada/${osAssistantCode}/log/`, {
                  headers: window.Utils.tokenAuthHeaders({
                    'Content-Type': 'application/json',
                  }),
                  method: 'POST',
                  body: JSON.stringify({ event: event, session_id: sessionId, org_id: orgId }),
                }).catch((error) => {
                  console.error('Error logging event:', error)
                })
              }
            }

            if (event.type === 'session.created') {
              setIsSessionCreated(true)
            } else if (event.type === 'response.done') {
              runFunctionCallsFromEvent(event)
              if (isFunctionCallOnlyResponseDone(event.response)) {
                isAssistantAudioPlayingRef.current = false
                setAssistantResponding(false)
              }
              clearReasoningPreamble()
              const outputs = event.response?.output || []
              if (isMountedRef.current && outputs.length > 0) {
                setLogs((prev) => {
                  const newLogs = [outputs[0], ...prev].slice(0, 10) // retain 10 most recent logs
                  return newLogs
                })
              }
              const savedTranscripts = new Set<string>()
              const addReceiveTranscription = (content: string) => {
                const trimmed = content.trim()
                if (!trimmed || savedTranscripts.has(trimmed)) {
                  return
                }
                savedTranscripts.add(trimmed)
                setSessionTranscriptions((prev) => [
                  ...prev,
                  {
                    content: trimmed,
                    direction: 'receive',
                    timestamp: Date.now(),
                    sharedWithContexts: [],
                  },
                ])
              }

              for (const output of outputs) {
                if (output?.type !== 'message' || !Array.isArray(output.content)) {
                  continue
                }
                let messageText = ''
                for (const content of output.content) {
                  messageText += extractMessageContentText(content)
                }
                addReceiveTranscription(messageText)
              }

              if (savedTranscripts.size === 0) {
                const fromResponse = extractAssistantTranscriptsFromResponseDone(event.response)
                const fromPending = pendingAssistantTranscriptRef.current.trim()
                addReceiveTranscription(fromResponse.length >= fromPending.length ? fromResponse : fromPending)
              }

              pendingAssistantTranscriptRef.current = ''
            } else if (event.type === 'conversation.item.input_audio_transcription.completed') {
              // Capture user transcription
              if (event.transcript && event.transcript.trim()) {
                setSessionTranscriptions((prev) => [
                  ...prev,
                  {
                    content: event.transcript.trim(),
                    direction: 'send',
                    timestamp: Date.now(),
                    sharedWithContexts: [],
                  },
                ])
                if (isMountedRef.current) {
                  userHasSpokenRef.current = true
                  setUserHasSpoken(true)
                  beginAssistantTurn()
                  sendClientEvent({
                    type: 'response.create',
                  })
                }
              }
            }
          } catch (err) {
            console.error('Error processing message:', err)
          }
        }

        if (isMountedRef.current) {
          setDataChannel(dc)
        }

        return dc
      } catch (error) {
        console.error('Error creating data channel:', error)
        return null
      }
    },
    [
      chatContextState,
      handleToolCallCallback,
      runFunctionCallsFromEvent,
      appendReasoningPreamble,
      appendSpeakingTranscriptTarget,
      beginAssistantTurn,
      clearReasoningPreamble,
      clearSpeakingTranscript,
      interruptAssistantIfSpeaking,
      resumeAssistantPlayback,
      setAssistantResponding,
      startSpeakingRevealTimer,
      stopSpeakingRevealTimer,
      sessionId,
      osAssistantCode,
      orgId,
    ]
  )

  // Update the startSession function to use the improved setupDataChannel
  const startSession = useCallback(
    async (osAssistantCode: string, orgId?: number, projectId?: number) => {
      console.log('startSession()')
      try {
        if (!isSessionStarted && !sessionOperationInProgressRef.current && isMountedRef.current) {
          sessionOperationInProgressRef.current = true
          setIsSessionStarted(true)
          orgIdRef.current = orgId ?? null
          osAssistantCodeRef.current = osAssistantCode
          setOrgId(orgId ?? null)
          setOsAssistantCode(osAssistantCode)
          setHasSentWelcomeMessage(false)
          setUserHasSpoken(false)
          userHasSpokenRef.current = false

          setSessionTranscriptions([])
          setSessionToolCalls([])

          // Get an ephemeral session token and realtimeClientConfig
          const sessionResponse = await osFetch(
            `${window.API_ROOT}/api/ada/${osAssistantCode}/sessions/?org_id=${orgId || ''}&project_id=${
              projectId || ''
            }`,
            {
              headers: window.Utils.tokenAuthHeaders(),
              method: 'POST',
            }
          )

          if (!sessionResponse.ok) {
            const errorBody = await sessionResponse.text()
            throw new Error(
              `Failed to create voice session (${sessionResponse.status}): ${errorBody.slice(0, 200)}`
            )
          }

          const session = await sessionResponse.json()

          if (!session?.session?.client_secret?.value) {
            throw new Error('Voice session response missing client secret')
          }

          const _realtimeClientConfig = session.realtime_client_config
          const mergedTools = _realtimeClientConfig.tools.concat(CLIENT_SIDE_TOOLS)
          processedFunctionCallIdsRef.current = new Set()
          currentSessionStateAppliedRef.current.tools = mergedTools
          setRealtimeClientConfig(_realtimeClientConfig)

          const sessionToken = session.session.client_secret.value

          console.log('Session id:', session.session.id)
          setSessionId(session.session.id)

          // Create a peer connection
          const pc = new RTCPeerConnection({
            iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
          })

          // Set up to play remote audio from the model
          if (!audioElement.current) {
            audioElement.current = document.createElement('audio')
          }
          audioElement.current.autoplay = true
          pc.ontrack = (e) => {
            if (audioElement.current) {
              audioElement.current.srcObject = e.streams[0]
              audioElement.current.play().catch((error) => {
                console.warn('[Voice] Failed to start assistant playback on track:', error)
              })
            }
          }

          // Handle connection state changes
          pc.oniceconnectionstatechange = () => {
            console.log('ICE connection state:', pc.iceConnectionState)
            if (
              pc.iceConnectionState === 'disconnected' ||
              pc.iceConnectionState === 'failed' ||
              pc.iceConnectionState === 'closed'
            ) {
              // Handle disconnection
              if (isMountedRef.current) {
                setIsSessionActive(false)
                setIsListening(false)
              }
            }
          }

          try {
            const stream = await navigator.mediaDevices.getUserMedia({
              audio: true,
            })

            stream.getTracks().forEach((track) => {
              const sender = pc.addTrack(track, stream)
              if (sender) {
                tracks.current = [...(tracks.current || []), sender]
              }
            })

            // Set up the data channel with our improved function
            setupDataChannel(pc, osAssistantCode, orgId || 0, session.session.id, _realtimeClientConfig, mergedTools)

            // Start the session using the Session Description Protocol (SDP)
            const offer = await pc.createOffer()
            await pc.setLocalDescription(offer)

            const sdpResponse = await fetch(_realtimeClientConfig.base_url, {
              method: 'POST',
              body: offer.sdp,
              headers: {
                Authorization: `Bearer ${sessionToken}`,
                'Content-Type': 'application/sdp',
              },
            })

            if (!sdpResponse.ok) {
              const errorBody = await sdpResponse.text()
              throw new Error(
                `Realtime SDP exchange failed (${sdpResponse.status}): ${errorBody.slice(0, 200)}`
              )
            }

            const answer: RTCSessionDescriptionInit = {
              type: 'answer',
              sdp: await sdpResponse.text(),
            }
            await pc.setRemoteDescription(answer)

            peerConnection.current = pc
          } catch (error) {
            console.error('Error setting up media connection:', error)
            setIsSessionStarted(false)
          } finally {
            sessionOperationInProgressRef.current = false
          }
        }
      } catch (error) {
        console.error('Error starting session:', error)
        setIsSessionStarted(false)
      } finally {
        sessionOperationInProgressRef.current = false
      }
    },
    [isSessionStarted, setupDataChannel, osAssistantCode, orgId]
  )

  // Stop current session, clean up peer connection and data channel
  const stopSession = useCallback(() => {
    if (sessionOperationInProgressRef.current) {
      console.log('Session operation already in progress, skipping stop')
      return
    }

    sessionOperationInProgressRef.current = true

    try {
      // Reset states first
      if (isMountedRef.current) {
        setIsSessionCreated(false)
        setIsSessionActive(false)
        setIsSessionStarted(false)
        isRespondingRef.current = false
        setIsResponding(false)
        setIsListening(false)
        reasoningStatusRef.current = null
        setReasoningStatus(null)
        setIsPreparingResponse(false)
        pendingAssistantTranscriptRef.current = ''
        isAssistantAudioPlayingRef.current = false
        isPreparingResponseRef.current = false
        setHasSentWelcomeMessage(false)
        setUserHasSpoken(false)
        userHasSpokenRef.current = false
        if (speakingRevealTimerRef.current) {
          clearInterval(speakingRevealTimerRef.current)
          speakingRevealTimerRef.current = null
        }
        speakingTranscriptTargetRef.current = ''
        speakingTranscriptDisplayRef.current = ''
        setSpeakingTranscript(null)
        setToolCall(null)
        setSessionId(null)
        orgIdRef.current = null
        osAssistantCodeRef.current = null
        setOrgId(null)
        setOsAssistantCode(null)
        setDynamicTools(CLIENT_SIDE_TOOLS)
        setRealtimeClientConfig(EMPTY_REALTIME_CLIENT_CONFIG)
      }

      const _dataChannel = getDataChannelInCallback()

      // Clean up data channel
      try {
        if (_dataChannel) {
          console.log('Closing data channel with state:', _dataChannel.readyState)

          // Try to gracefully close the data channel before removing event listeners
          if (_dataChannel.readyState === 'open') {
            _dataChannel.close()
          }

          if (isMountedRef.current) {
            setDataChannel(null)
          }
        }
      } catch (error) {
        console.error('Error closing data channel:', error)
      }

      // Clean up peer connection
      try {
        if (peerConnection.current) {
          // Stop all tracks in the peer connection
          peerConnection.current.getSenders().forEach((sender) => {
            try {
              if (sender.track) {
                sender.track.stop()
              }
            } catch (error) {
              console.error('Error stopping sender track:', error)
            }
          })

          peerConnection.current.getReceivers().forEach((receiver) => {
            try {
              if (receiver.track) {
                receiver.track.stop()
              }
            } catch (error) {
              console.error('Error stopping receiver track:', error)
            }
          })

          try {
            peerConnection.current.close()
          } catch (error) {
            console.error('Error closing peer connection:', error)
          }

          peerConnection.current = null
        }
      } catch (error) {
        console.error('Error cleaning up peer connection:', error)
      }

      // Stop all tracks in the audio stream
      try {
        const currentAudioStream = audioStreamRef.current
        if (currentAudioStream) {
          currentAudioStream.getTracks().forEach((track) => {
            try {
              track.stop()
              track.enabled = false
            } catch (error) {
              console.error('Error stopping audio track:', error)
            }
          })

          if (isMountedRef.current) {
            setAudioStream(null)
            setIsListening(false)
          }
        }
      } catch (error) {
        console.error('Error stopping audio stream:', error)
      }

      // Clean up audio element
      try {
        if (audioElement.current) {
          audioElement.current.pause()
          audioElement.current.srcObject = null
        }
      } catch (error) {
        console.error('Error cleaning up audio element:', error)
      }

      audioTransceiver.current = null
      tracks.current = null

      // Save current session data before clearing refs (for importVoiceToChat)
      const currentTranscriptions = sessionTranscriptionsRef.current
      const currentToolCalls = sessionToolCallsRef.current

      // Reset all refs to clean state
      sessionTranscriptionsRef.current = []
      sessionToolCallsRef.current = []
      dataChannelRef.current = null
      audioStreamRef.current = null

      // Cleanup local tracking of session state
      currentSessionStateAppliedRef.current = getDefaultChatContextState()
      processedFunctionCallIdsRef.current = new Set()

      // Import voice session data to chat (must happen after saving data but before clearing refs)
      importVoiceToChat(currentTranscriptions, currentToolCalls, setSessionTranscriptions, setSessionToolCalls)
    } finally {
      sessionOperationInProgressRef.current = false
    }
  }, [])

  // Replace the old effect with a simplified version that just monitors dataChannel state changes
  useEffect(() => {
    // We don't need to add message or open listeners here anymore as they're handled in setupDataChannel

    if (dataChannel) {
      const stateChangeHandler = () => {
        console.log('DataChannel state changed to:', dataChannel.readyState)
      }

      dataChannel.addEventListener('open', stateChangeHandler)
      dataChannel.addEventListener('close', stateChangeHandler)

      return () => {
        dataChannel.removeEventListener('open', stateChangeHandler)
        dataChannel.removeEventListener('close', stateChangeHandler)
      }
    }
  }, [dataChannel])

  // Listen for Redux action to stop voice session
  // VoiceProvider is rendered outside Redux Provider, so we use window.reduxStore directly
  useEffect(() => {
    if (!window.reduxStore) return

    let previousVoiceSessionActive = window.reduxStore.getState()?.chatDrawer?.voiceSessionActive

    const unsubscribe = window.reduxStore.subscribe(() => {
      const state = window.reduxStore.getState()
      const currentVoiceSessionActive = state?.chatDrawer?.voiceSessionActive

      if (previousVoiceSessionActive === true && currentVoiceSessionActive === false && isSessionActive) {
        stopSession()
      }

      previousVoiceSessionActive = currentVoiceSessionActive
    })

    return () => {
      unsubscribe()
    }
  }, [stopSession, isSessionActive])

  // Simplified cleanup on unmount
  useEffect(() => {
    return () => {
      stopSession()
    }
  }, [stopSession])

  // Grabs a new mic track and replaces the placeholder track in the transceiver
  const startListening = useCallback(async () => {
    try {
      const newStream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      })
      if (isMountedRef.current) {
        setAudioStream(newStream)
      }

      // If we already have an audioSender, just replace its track:
      if (tracks.current) {
        const micTrack = newStream.getAudioTracks()[0]
        tracks.current.forEach((sender) => {
          sender.replaceTrack(micTrack)
        })
      } else if (peerConnection.current) {
        // Fallback if audioSender somehow didn't get set
        newStream.getTracks().forEach((track) => {
          const sender = peerConnection.current?.addTrack(track, newStream)
          if (sender) {
            tracks.current = [...(tracks.current || []), sender]
          }
        })
      }

      setIsListening(true)
    } catch (error) {
      console.error('Error accessing microphone:', error)
    }
  }, [])

  // Replaces the mic track with a placeholder track
  const stopListening = useCallback(() => {
    setIsListening(false)

    // Stop existing mic tracks so the user's mic is off
    const currentAudioStream = audioStreamRef.current
    if (currentAudioStream) {
      currentAudioStream.getTracks().forEach((track) => track.stop())
    }
    if (isMountedRef.current) {
      setAudioStream(null)
    }

    // Replace with a placeholder (silent) track
    if (tracks.current) {
      const placeholderTrack = createEmptyAudioTrack()
      tracks.current.forEach((sender) => {
        sender.replaceTrack(placeholderTrack)
      })
    }
  }, [])

  // Creates a placeholder track that is silent
  function createEmptyAudioTrack(): MediaStreamTrack {
    const audioContext = new AudioContext()
    const destination = audioContext.createMediaStreamDestination()
    return destination.stream.getAudioTracks()[0]
  }

  useEffect(() => {
    if (isSessionActive && dataChannel?.readyState === 'open' && !hasSentWelcomeMessage) {
      // Prompt a welcome message from the assistant
      try {
        // Send explicit welcome instruction as a system message
        sendClientEvent({
          type: 'conversation.item.create',
          item: {
            type: 'message',
            role: 'system',
            content: [
              {
                type: 'input_text',
                text: `Give a brief, friendly welcome greeting. Introduce yourself as Ada and ask how you can help today. Do not respond to any context or app state - this should be a fresh greeting. Speak in the same language as the user, ${realtimeClientConfig.language}.`,
              },
            ],
          },
        })

        // Then trigger the response
        sendClientEvent({
          type: 'response.create',
        })

        if (isMountedRef.current) {
          setHasSentWelcomeMessage(true)
        }
      } catch (error) {
        console.error('Error sending welcome message:', error)
      }
    }
  }, [isSessionActive, dataChannel?.readyState, hasSentWelcomeMessage, sendClientEvent, realtimeClientConfig.language])

  useEffect(() => {
    // We add isSessionCreated to avoid firing this until we get confirmation that the session was created
    if (
      isSessionActive &&
      isSessionCreated &&
      dataChannel?.readyState === 'open' &&
      Object.keys(chatContextState).length > 0
    ) {
      if (currentSessionStateAppliedRef.current.app_state !== chatContextState) {
        console.log(
          'Sending chat context state to session',
          currentSessionStateAppliedRef.current.app_state,
          chatContextState
        )

        try {
          // @TODO: Consider if perhaps we can only send the changed APP_STATE values to reduce data/token transfer.
          sendClientEvent({
            type: 'conversation.item.create',
            item: {
              type: 'message',
              role: 'system',
              content: [
                {
                  type: 'input_text',
                  text: JSON.stringify({ APP_STATE: chatContextState }),
                },
              ],
            },
          })

          currentSessionStateAppliedRef.current.app_state = chatContextState
        } catch (error) {
          console.error('Error sending chat context state:', error)
        }
      } else {
        console.log(
          'No changes to chat context state',
          currentSessionStateAppliedRef.current.app_state,
          chatContextState
        )
      }
    }
  }, [chatContextState, isSessionActive, isSessionCreated, dataChannel?.readyState, sendClientEvent, userHasSpoken])

  const updateState = useCallback(
    (state: Partial<{ os_ada_code: string; project_id: number; org_id: number; pathname: string }>) => {
      if (isMountedRef.current) {
        if (state.org_id !== undefined) {
          setOrgId(state.org_id)
        }
        if (state.os_ada_code !== undefined) {
          setOsAssistantCode(state.os_ada_code)
        }
        setChatContextState((prev) => ({
          ...prev,
          ...state,
        }))
      }
    },
    []
  )

  const voiceContextValue = {
    isSessionActive,
    isListening,
    isConnecting,
    isResponding,
    isPreparingResponse,
    reasoningStatus,
    speakingTranscript,
    startSession,
    stopSession,
    startListening,
    stopListening,
    logs,
    toolCall,
    updateState,
    clearSessionState: useCallback(() => {
      currentSessionStateAppliedRef.current = getDefaultChatContextState()
    }, []),
  }

  //Expose to the window to enable tools to call this directly.
  if (typeof window !== 'undefined') {
    ;(window as any).voiceStopSession = stopSession
  }

  return <VoiceContext.Provider value={voiceContextValue}>{children}</VoiceContext.Provider>
}

export default VoiceProvider
